import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "./Button";
import { Motif } from "./Motif";
import { IMAGES } from "../../data/site";
import { scrollToId } from "../../data/site";

const lines = ["Ruimte voor", "wie je bent."];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = (delay) => ({
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] } },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, -48]);

  return (
    <section ref={ref} id="home" className="relative overflow-hidden pt-28 md:pt-32 pb-16 md:pb-24">
      {/* background motifs */}
      <div className="pointer-events-none absolute -left-40 -top-24 w-[520px] h-[520px] -z-10">
        <Motif opacity={0.16} />
      </div>
      <div className="pointer-events-none absolute right-[-6rem] bottom-[-4rem] w-[360px] h-[360px] -z-10">
        <Motif opacity={0.1} flip />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          variants={fade(0.15)}
          initial="hidden"
          animate="show"
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-px w-8 bg-kern-terracotta" />
          <span className="text-xs tracking-[0.28em] uppercase text-kern-terracotta font-medium">
            Welkom bij De Kern
          </span>
        </motion.div>

        <h1 className="font-serif text-[2.6rem] leading-[1.02] sm:text-6xl sm:leading-[0.98] lg:text-8xl lg:leading-[0.95] tracking-tight text-kern-dark">
          {lines.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span
                className="block pb-[0.12em]"
                variants={lineVariants}
                custom={i}
                initial="hidden"
                animate="show"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-start mt-12 lg:mt-16">
          {/* Left */}
          <div className="order-2 lg:order-1">
            <motion.p
              variants={fade(0.85)}
              initial="hidden"
              animate="show"
              className="font-serif italic text-lg sm:text-2xl md:text-3xl text-kern-olive"
            >
              Een plek waar kinderen, jongeren, ouders en volwassenen welkom zijn met hun verhaal.
            </motion.p>

            <motion.div
              variants={fade(1)}
              initial="hidden"
              animate="show"
              className="mt-4 max-w-xl space-y-3.5 text-sm sm:text-base text-kern-taupe leading-relaxed"
            >
              <p>
                Soms merk je dat je kind niet goed in zijn vel zit. Soms bots je als ouder
                op onzekerheid, stress of vragen waarop je geen antwoord lijkt te vinden.
                En soms voel je dat je zelf uit balans bent en op zoek bent naar meer rust,
                veerkracht en vertrouwen.
              </p>
              <p>
                Bij De Kern help ik je graag om daar verandering in te brengen. We
                vertrekken vanuit jouw vragen en noden. Samen gaan we op zoek om opnieuw
                verbinding te voelen en vertrouwen te krijgen in jezelf, in je kind en in
                de toekomst. Echte verandering ontstaat wanneer we durven stilstaan bij wat
                er vanbinnen leeft. Wanneer we tijd nemen om te ontdekken wat iemand nodig
                heeft, welke krachten al aanwezig zijn en waar opnieuw beweging kan ontstaan.
              </p>
            </motion.div>

            <motion.div variants={fade(1.15)} initial="hidden" animate="show" className="mt-9">
              <Button as="button" data-testid="hero-cta" size="lg"
                onClick={() => scrollToId("#contact")}>
                Maak een afspraak
              </Button>
            </motion.div>
          </div>

        {/* Right — organic arch frame with parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex justify-center lg:justify-end lg:-mt-52 order-1 lg:order-2 mb-4 lg:mb-0"
        >
          {/* Portret in de vorm van het logo: geneste cirkels rond een ronde foto */}
          <motion.div style={{ y: portraitY }} className="relative w-[80%] max-w-[336px] sm:w-[72%] sm:max-w-[380px] lg:w-[88%] lg:max-w-[506px] aspect-square">
            {/* sage buitenring */}
            <div className="absolute inset-0 rounded-full bg-kern-sage" aria-hidden="true" />
            {/* olijfgroene ring, verschoven zodat er bovenaan een halve maan ontstaat */}
            <div
              className="absolute rounded-full bg-kern-olive"
              style={{ top: "10%", left: "6%", right: "6%", bottom: "2%" }}
              aria-hidden="true"
            />
            {/* terracotta accentring */}
            <div
              className="absolute rounded-full bg-kern-terracotta"
              style={{ top: "17%", left: "12%", right: "12%", bottom: "8%" }}
              aria-hidden="true"
            />
            {/* ronde foto van Griet */}
            <div
              style={{ top: "19%", left: "14%", right: "14%", bottom: "10%" }}
              className="absolute rounded-full overflow-hidden shadow-[0_20px_60px_rgba(79,80,72,0.2)]"
            >
              <img
                src="/griet-hero-v2.webp"
                alt="Griet Quirynen, therapeut en coach bij Praktijk De Kern"
                loading="eager"
                className="w-full h-full object-cover"
                style={{ objectPosition: "52% 42%" }}
              />
            </div>

            {/* naambadge (enkel desktop) */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="hidden lg:block absolute bottom-2 left-0 bg-white rounded-2xl px-5 py-3 shadow-[0_16px_50px_rgba(79,80,72,0.14)]"
            >
              <div className="font-serif text-xl text-kern-dark leading-none">Griet Quirynen</div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-kern-taupe mt-1.5">
                Gezinstherapeut
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
        </div>
      </div>
    </section>
  );
};
